import { QUERY_KEYS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { ErrorService } from '@avanta/shared/services/error';
import { type TableDateRange } from '@avanta/shared/types/table';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { type ColumnDef, type Table, type Updater } from '@tanstack/react-table';
import { isAxiosError } from 'axios';
import { format, startOfYear } from 'date-fns';
import { useState } from 'react';
import { EnterprisesService } from 'services/enterprises';
import { useUserStore } from 'stores/user';
import { type EnterpriseInvoice, type EnterpriseInvoicesResponse } from 'types/enterprises';

const PAGE_SIZE = 10;
const MISSING_CIF_STATUS = 406;

const getDefaultDateRange = (): TableDateRange => ({
  from: startOfYear(new Date()),
  to: new Date(),
});

const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

const isMissingCifError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === MISSING_CIF_STATUS;

export const useFetchInvoices = (
  columns: ColumnDef<EnterpriseInvoice>[]
): {
  table: Table<EnterpriseInvoice>;
  query: UseQueryResult<EnterpriseInvoicesResponse, Error>;
  selectedDateRange: TableDateRange | undefined;
  setDateRange: (value: TableDateRange | undefined) => void;
  isMissingCif: boolean;
} => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState<TableDateRange | undefined>(
    getDefaultDateRange
  );
  const insuranceId = useUserStore(state => state.user?.id) ?? '';
  const debouncedSearch = useDebounce(globalFilter, 500);
  const dateFrom = selectedDateRange?.from ? toIsoDate(selectedDateRange.from) : undefined;
  const dateTo = selectedDateRange?.to ? toIsoDate(selectedDateRange.to) : undefined;

  const resetPagination = (): void => {
    setPagination(previousPagination =>
      previousPagination.pageIndex === 0
        ? previousPagination
        : { ...previousPagination, pageIndex: 0 }
    );
  };

  const handleGlobalFilterChange = (updater: Updater<string>): void => {
    setGlobalFilter(updater);
    resetPagination();
  };

  const setDateRange = (value: TableDateRange | undefined): void => {
    setSelectedDateRange(value);
    resetPagination();
  };

  const query = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_ENTERPRISE_INVOICES,
      insuranceId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedSearch,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      try {
        const { data } = await EnterprisesService.getInvoices(insuranceId, {
          offset: pagination.pageIndex,
          limit: pagination.pageSize,
          dateFrom,
          dateTo,
          search: debouncedSearch || undefined,
        });
        return data;
      } catch (error) {
        if (!isMissingCifError(error) && isAxiosError(error)) {
          ErrorService.handlerErrorRequest(error);
        }
        throw error;
      }
    },
    placeholderData: previousData => previousData,
    enabled: !!insuranceId,
    retry: (failureCount, error) => !isMissingCifError(error) && failureCount < 2,
  });

  const table = useServerPaginatedTable<EnterpriseInvoice>({
    columns,
    data: query.data?.invoices ?? [],
    totalCount: query.data?.total ?? 0,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: handleGlobalFilterChange,
  });

  return {
    table,
    query,
    selectedDateRange,
    setDateRange,
    isMissingCif: isMissingCifError(query.error),
  };
};
